import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
    EInvoiceDraftLineMeta,
    EInvoiceDraftLineRow,
    formatDraftNumber,
} from './einvoice-draft-line-row'
import { EInvoiceDraftRemovedLine } from './einvoice-draft-removed-line'
import type { EInvoiceDraftItem } from '@/lib/api/einvoice-api'
import type { EInvoiceDraftValues } from '@/lib/schemas/einvoice-draft-schemas'
import { useHorizontalOverflow } from '@/hooks/use-horizontal-overflow'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { m } from '@/lib/paraglide/messages'

export const editableDraftItem = (item: EInvoiceDraftItem) =>
    item.item_type === 1 || item.item_type === 2

// Discount and note rows are built by the server and stay read-only.
function ReadonlyLineRow({ item }: { item: EInvoiceDraftItem }) {
    return (
        <TableRow className="align-top">
            <TableCell className="text-muted-foreground tabular-nums">
                {item.line_number}
            </TableCell>
            <TableCell className="whitespace-normal">
                <p className="break-words">
                    {item.item_name} · {m.einvoice_draft_readonly_line()}
                </p>
                {item.quantity > 0 && <EInvoiceDraftLineMeta item={item} />}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatDraftNumber(item.amount)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatDraftNumber(item.discount_percent)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatDraftNumber(item.discount_amount)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatDraftNumber(item.vat_amount)}
            </TableCell>
            <TableCell className="text-right font-medium tabular-nums">
                {formatDraftNumber(
                    item.amount - item.discount_amount + item.vat_amount,
                )}
            </TableCell>
            <TableCell />
        </TableRow>
    )
}

export function EInvoiceDraftLines({ items }: { items: EInvoiceDraftItem[] }) {
    const form = useFormContext<EInvoiceDraftValues>()
    const revalidate = {
        shouldDirty: true,
        shouldValidate: form.formState.isSubmitted,
    }
    // Removing resets the line to its saved values so a hidden line can never
    // hold an invalid edit that blocks the save.
    const remove = (index: number, item: EInvoiceDraftItem) => {
        form.setValue(
            `items.${index}`,
            {
                line_number: item.line_number,
                item_name: item.item_name,
                discount_percent: item.discount_percent,
                vat_amount: item.vat_amount,
                removed: true,
            },
            revalidate,
        )
        form.clearErrors(`items.${index}`)
    }
    // The Table primitive owns the scroll wrapper; reach it through <table>.
    const [scroller, setScroller] = useState<HTMLElement | null>(null)
    const overflowing = useHorizontalOverflow(scroller)
    const tableRef = useCallback(
        (table: HTMLTableElement | null) =>
            setScroller(table?.parentElement ?? null),
        [],
    )
    const lines = form.watch('items')
    const allRemoved = lines.length > 0 && lines.every((line) => line.removed)
    let editableIndex = -1
    return (
        <div className="space-y-3">
            {overflowing && (
                <p className="text-xs text-muted-foreground">
                    {m.einvoice_draft_lines_scroll_hint()}
                </p>
            )}
            <div className="overflow-hidden rounded-lg border">
                <Table ref={tableRef} className="min-w-[760px]">
                    <caption className="sr-only">
                        {m.einvoice_draft_lines_caption()}
                    </caption>
                    <TableHeader className="bg-muted/50">
                        <TableRow className="hover:bg-transparent">
                            <TableHead className="w-12">
                                {m.einvoice_draft_col_line()}
                            </TableHead>
                            <TableHead>
                                {m.einvoice_draft_item_name()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.einvoice_draft_col_amount()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.einvoice_draft_discount_percent()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.einvoice_draft_col_discount()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.einvoice_draft_vat_amount()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.einvoice_draft_col_total()}
                            </TableHead>
                            <TableHead className="w-14">
                                <span className="sr-only">
                                    {m.einvoice_draft_col_actions()}
                                </span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((item) => {
                            if (!editableDraftItem(item))
                                return (
                                    <ReadonlyLineRow
                                        key={item.line_number}
                                        item={item}
                                    />
                                )
                            const index = ++editableIndex
                            return lines[index]?.removed ? (
                                <EInvoiceDraftRemovedLine
                                    key={item.line_number}
                                    item={item}
                                    onRestore={() =>
                                        form.setValue(
                                            `items.${index}.removed`,
                                            false,
                                            revalidate,
                                        )
                                    }
                                />
                            ) : (
                                <EInvoiceDraftLineRow
                                    key={item.line_number}
                                    item={item}
                                    index={index}
                                    onRemove={() => remove(index, item)}
                                />
                            )
                        })}
                    </TableBody>
                </Table>
            </div>
            {allRemoved && (
                <p role="alert" className="text-sm text-destructive">
                    {m.einvoice_draft_lines_required()}
                </p>
            )}
            <p className="text-sm text-muted-foreground">
                {m.einvoice_draft_vat_hint()}
            </p>
        </div>
    )
}
